import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CalendarRange, Clock3, Link2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { parseApiError } from '@ooh/contracts';
import type { Offer, UpdateOffer } from '@ooh/contracts';
import { Badge } from '@ooh/ui/components/badge';
import { SharingDialog } from '@ooh/screens';
import { Button } from '@ooh/ui/components/button';
import { toast } from 'sonner';
import { formatDisplayDateRange } from '../../../lib/date-format';
import { offerKeys, useUpdateOffer } from '../api';
import { getOfferStatusLabelKey } from '../lib/offer-status';
import { ClientShareDialog } from './client-share-dialog';
import { EditDrawer } from './edit-drawer';
import { HeaderActionsMenu } from './header-actions-menu';
import { OfferTitleInlineEdit } from './offer-title-inline-edit';
import { TrashConfirmDialog } from './trash-confirm-dialog';

interface OfferHeaderProps {
  canAdminChangeStatus?: boolean;
  canEdit?: boolean;
  canManagePublicLink?: boolean;
  canManageSharing?: boolean;
  canTrash?: boolean;
  clientBrands?: string[];
  clientName: string;
  isReadOnly?: boolean;
  offer: Offer;
}

function StatusRow({
  campaignNumber,
  campaignNumberLabel,
  statusLabel,
}: {
  campaignNumber: string | null;
  campaignNumberLabel: string;
  statusLabel: string;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Badge variant="outline" className="shrink-0 text-xs">
        {statusLabel}
      </Badge>
      {campaignNumber ? (
        <CampaignNumberBadge campaignNumber={campaignNumber} label={campaignNumberLabel} />
      ) : null}
    </div>
  );
}

function HeaderTitleRow({
  canEdit,
  disabled,
  onSave,
  title,
  titleInputLabel,
}: {
  canEdit: boolean;
  disabled: boolean;
  onSave: (title: string) => Promise<void>;
  title: string;
  titleInputLabel: string;
}) {
  return (
    <OfferTitleInlineEdit
      canEdit={canEdit}
      disabled={disabled}
      inputLabel={titleInputLabel}
      title={title}
      onSave={onSave}
    />
  );
}

function useOfferHeaderEditing({
  conflictMessage,
  offer,
  saveErrorMessage,
  savedMessage,
}: {
  conflictMessage: string;
  offer: Offer;
  saveErrorMessage: string;
  savedMessage: string;
}) {
  const queryClient = useQueryClient();
  const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);
  const [isSharingDialogOpen, setIsSharingDialogOpen] = React.useState(false);
  const [isTrashDialogOpen, setIsTrashDialogOpen] = React.useState(false);
  const [resetToken, setResetToken] = React.useState(0);
  const updateOffer = useUpdateOffer();

  async function handleConflict() {
    toast.error(conflictMessage);
    await queryClient.invalidateQueries({ queryKey: offerKeys.detail(String(offer.id)) });
    setResetToken((current) => current + 1);
  }

  async function handleInlineTitleSave(title: string) {
    try {
      await updateOffer.mutateAsync({ id: String(offer.id), title, updatedAt: offer.updatedAt });
    } catch (error) {
      await handleOfferMutationError({
        error,
        handleConflict,
        saveErrorMessage,
      });
    }
  }

  async function handleDrawerSubmit(values: UpdateOffer) {
    try {
      await updateOffer.mutateAsync({
        ...values,
        id: String(offer.id),
        updatedAt: offer.updatedAt,
      });
      toast.success(savedMessage);
      setIsDrawerOpen(false);
    } catch (error) {
      await handleOfferMutationError({
        error,
        handleConflict,
        saveErrorMessage,
      });
    }
  }

  return {
    handleDrawerSubmit,
    handleInlineTitleSave,
    isDrawerOpen,
    isSharingDialogOpen,
    isTrashDialogOpen,
    resetToken,
    setIsDrawerOpen,
    setIsSharingDialogOpen,
    setIsTrashDialogOpen,
    updateOffer,
  };
}

async function handleOfferMutationError({
  error,
  handleConflict,
  saveErrorMessage,
}: {
  error: unknown;
  handleConflict: () => Promise<void>;
  saveErrorMessage: string;
}) {
  if (parseApiError(error).statusCode === 409) {
    await handleConflict();
    return;
  }

  toast.error(saveErrorMessage);
}

/**
 * Exposes the offer's campaign duration (defaultDateFrom/defaultDateTo) directly on the
 * workspace header instead of only inside the "..." menu's edit drawer, and opens that
 * same drawer on click. QA (07/29 session) reported Szymon searching the "..." menu
 * twice and not finding this field. CU-86caypxab.
 */
function CampaignDurationTrigger({
  defaultDateFrom,
  defaultDateTo,
  disabled,
  onClick,
}: {
  defaultDateFrom: Date | null;
  defaultDateTo: Date | null;
  disabled: boolean;
  onClick: () => void;
}) {
  const { t } = useTranslation('offers');
  const label =
    defaultDateFrom && defaultDateTo
      ? formatDisplayDateRange(defaultDateFrom, defaultDateTo)
      : t('workspace.header.campaignDurationUnset');

  return (
    <Button
      className="inline-flex items-center gap-1.5 hover:text-foreground"
      data-testid="offer-campaign-duration-trigger"
      disabled={disabled}
      type="button"
      variant="inline"
      onClick={onClick}
    >
      <CalendarRange className="h-3.5 w-3.5" />
      <span>{label}</span>
    </Button>
  );
}

function ClientBrandLabel({ brand }: { brand: string | null }) {
  return brand ? <span>{brand}</span> : null;
}

function CampaignNumberBadge({ campaignNumber, label }: { campaignNumber: string; label: string }) {
  return (
    <Badge className="gap-2 px-2.5 py-1 text-xs" variant="success">
      <span className="font-medium">{label}</span>
      <span className="font-mono font-semibold">{campaignNumber}</span>
    </Badge>
  );
}

function getValidityCountdown(
  validUntil: Date,
  t: ReturnType<typeof useTranslation<'offers'>>['t'],
) {
  const millisecondsPerDay = 24 * 60 * 60 * 1000;
  const daysLeft = Math.max(0, Math.ceil((validUntil.getTime() - Date.now()) / millisecondsPerDay));

  return t('workspace.header.validityCountdown', { days: daysLeft });
}

function deriveHeaderAccess({
  canEdit,
  canManageSharing,
  canTrash,
  isReadOnly,
}: Pick<OfferHeaderProps, 'canEdit' | 'canManageSharing' | 'canTrash' | 'isReadOnly'>) {
  return {
    isEditable: (canEdit ?? true) && !isReadOnly,
    canOpenSharing: canManageSharing ? !isReadOnly : false,
    canMoveToTrash: canTrash ? !isReadOnly : false,
  };
}

export interface OfferHeaderHandle {
  /** Imperatively opens the campaign-dates editor drawer — see CampaignDurationTrigger
   * and the availability tab's no-default-date-range CTA (CU-86caypxa6 / CU-86caypxab). */
  openEditDrawer: () => void;
  /** Imperatively opens the "Udostępnij klientowi" dialog — wired from the Wersje tab's
   * active-version card so sharing sits next to its preview button, not only at the top
   * of the workspace (CU-86caypxpc). */
  openClientShareDialog: () => void;
}

export const OfferHeader = React.forwardRef<OfferHeaderHandle, OfferHeaderProps>(
  function OfferHeader(
    {
      canAdminChangeStatus = false,
      canEdit = true,
      canManagePublicLink = false,
      canManageSharing = false,
      canTrash = false,
      clientBrands = [],
      clientName,
      isReadOnly = false,
      offer,
    },
    ref,
  ) {
    const { t } = useTranslation('offers');
    const [isClientShareOpen, setIsClientShareOpen] = React.useState(false);
    const {
      handleDrawerSubmit,
      handleInlineTitleSave,
      isDrawerOpen,
      isSharingDialogOpen,
      isTrashDialogOpen,
      resetToken,
      setIsDrawerOpen,
      setIsSharingDialogOpen,
      setIsTrashDialogOpen,
      updateOffer,
    } = useOfferHeaderEditing({
      conflictMessage: t('workspace.header.toasts.conflict'),
      offer,
      saveErrorMessage: t('workspace.header.toasts.saveError'),
      savedMessage: t('workspace.header.toasts.saved'),
    });
    const { isEditable, canOpenSharing, canMoveToTrash } = deriveHeaderAccess({
      canEdit,
      canManageSharing,
      canTrash,
      isReadOnly,
    });

    React.useImperativeHandle(ref, () => ({
      openEditDrawer: () => setIsDrawerOpen(true),
      openClientShareDialog: () => setIsClientShareOpen(true),
    }));

    return (
      <>
        <header
          className="border-b border-border bg-background px-6 py-3"
          data-offer-status={offer.status}
          data-read-only={String(isReadOnly)}
          data-testid="offer-workspace-header"
        >
          <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
            <div className="min-w-0 space-y-1.5">
              <StatusRow
                campaignNumber={offer.campaignNumber}
                campaignNumberLabel={t('workspace.header.campaignNumber')}
                statusLabel={t(getOfferStatusLabelKey(offer.status))}
              />
              <HeaderTitleRow
                canEdit={isEditable}
                disabled={updateOffer.isPending}
                title={offer.title}
                titleInputLabel={t('workspace.header.fields.title')}
                onSave={handleInlineTitleSave}
              />
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                <span>{clientName}</span>
                <ClientBrandLabel brand={offer.brand} />
                <span
                  className="inline-flex items-center gap-1.5"
                  data-testid="offer-validity-countdown"
                >
                  <Clock3 className="h-3.5 w-3.5" />
                  <span>{getValidityCountdown(offer.validUntil, t)}</span>
                </span>
                <CampaignDurationTrigger
                  defaultDateFrom={offer.defaultDateFrom}
                  defaultDateTo={offer.defaultDateTo}
                  disabled={!isEditable}
                  onClick={() => setIsDrawerOpen(true)}
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-end gap-2">
              <Button
                className="gap-1.5"
                data-testid="client-share-btn"
                onClick={() => setIsClientShareOpen(true)}
                size="sm"
                type="button"
                variant="outline"
              >
                <Link2 className="h-3.5 w-3.5" />
                {t('workspace.header.clientShare.trigger')}
              </Button>
              <HeaderActionsMenu
                canAdminChangeStatus={canAdminChangeStatus}
                canEdit={isEditable}
                canManageSharing={canOpenSharing}
                canTrash={canMoveToTrash}
                isReadOnly={isReadOnly}
                offer={offer}
                onClientShare={() => setIsClientShareOpen(true)}
                onEdit={() => setIsDrawerOpen(true)}
                onManageSharing={() => setIsSharingDialogOpen(true)}
                onMoveToTrash={() => setIsTrashDialogOpen(true)}
              />
            </div>
          </div>
        </header>
        <EditDrawer
          clientBrands={clientBrands}
          isOpen={isDrawerOpen}
          isPending={updateOffer.isPending}
          offer={offer}
          resetToken={resetToken}
          onClose={() => setIsDrawerOpen(false)}
          onSubmit={handleDrawerSubmit}
        />
        <SharingDialog
          isOpen={isSharingDialogOpen}
          offerId={String(offer.id)}
          onClose={() => setIsSharingDialogOpen(false)}
        />
        <TrashConfirmDialog
          isOpen={isTrashDialogOpen}
          offerId={String(offer.id)}
          onClose={() => setIsTrashDialogOpen(false)}
        />
        <ClientShareDialog
          canManagePublicLink={canManagePublicLink}
          clientPreviewHref={`/offers/${String(offer.id)}/client`}
          isOpen={isClientShareOpen}
          offer={offer}
          offerId={String(offer.id)}
          offerTitle={offer.title}
          onClose={() => setIsClientShareOpen(false)}
        />
      </>
    );
  },
);
